"use client"

import { fetchTourAll } from "@/(admin)/modules/tour/services/tours"
import { t } from "@/languages/i18n"
import { getSettings } from "@/lib/navigation-services"
import { useNotificationMessages } from "@/lib/useNotificationMessages"
import { cn } from "@/lib/utils"
import { TourDetail, TourDetailData } from "@/types/tour"
import { BikeTypeOption, DataSettingItem } from "@/types/types"
import { LoadingOutlined } from "@ant-design/icons"
import { DatePicker, Form, Input, Modal, Select, Spin } from "antd"
import enUS from "antd/es/calendar/locale/en_US"
import viVN from "antd/es/calendar/locale/vi_VN"
import { Rule } from "antd/es/form"
import dayjs from "dayjs"
import { CountryCode, getCountries, getCountryCallingCode } from "libphonenumber-js"
import { ChevronDownIcon, Minus, Plus, X } from "lucide-react"
import { ReactNode, useEffect, useMemo, useRef, useState } from "react"

const InfomationFormTour = ({
  label,
  inputs,
}: {
  label: string
  inputs: {
    name?: string
    className?: string
    input?: ReactNode
    rules?: Rule[]
    custom?: ReactNode
  }[]
}) => {
  return (
    <div className="flex flex-col gap-4">
      <p className="text-base leading-6 font-semibold text-[#242F3E] uppercase">{label}</p>

      {inputs.map((item, index) => {
        if (item.custom) return <div key={`${label}-${index}`}>{item.custom}</div>

        return (
          <Form.Item
            key={`${label}-${item.name}`}
            name={item?.name}
            className={cn("mb-0", item.className)}
            rules={item.rules}
          >
            {item.input}
          </Form.Item>
        )
      })}
    </div>
  )
}

const ScrollIndicator = ({ canScrollDown }: { canScrollDown: boolean }) => {
  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-10 pointer-events-none ${
        canScrollDown ? "opacity-100" : "opacity-0"
      } transition-all duration-200`}
    >
      <div className="bg-gradient-to-t from-[#eeeeee] via-white/50 to-transparent h-16 w-full"></div>
    </div>
  )
}

const FormBooking = ({
  isModalOpen,
  currentTour,
  locale,
  setIsModalOpen,
}: {
  isModalOpen: boolean
  currentTour: TourDetail
  setIsModalOpen: (isModalOpen: boolean) => void
  locale?: string
}) => {
  const [form] = Form.useForm()
  const [tour, setTour] = useState<TourDetailData[]>([])
  const [loading, setLoading] = useState(false)

  const [emailConfig, setEmailConfig] = useState("")
  const [adultQuantity, setAdultQuantity] = useState<number | undefined>(1)
  const [childrenQuantity, setChildrenQuantity] = useState<number | undefined>(undefined)
  const [canScrollDown, setCanScrollDown] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState<string>("VN")
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const countries = useMemo(() => getCountries(), [])

  const { successNotification, errorNotification, contextHolderNotification } = useNotificationMessages()

  const fetchTours = async () => {
    try {
      const response = await fetchTourAll({
        locale: locale ?? "en",
        publicationState: "live",
      })
      setTour(response.data)
    } catch (error) {
      console.error("Error fetching tour:", error)
    }
  }

  const getTimeOptions = () => {
    return Array.from({ length: 24 }, (_, i) => ({
      label: `${i}:00`,
      value: `${i}:00`,
    }))
  }

  const calcTotalPrice = (currentTour?.price_overview ?? 0) * ((adultQuantity ?? 0) + (childrenQuantity ?? 0))

  const sendEmailBookTour = async () => {
    setLoading(true)
    try {
      await form.validateFields()

      const response = await fetch("/api/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          subject: `[Thông báo] Đăng ký tour ${form.getFieldValue("tour")}`,
          emailTo: emailConfig,
          content: `
                Loại tour: ${form.getFieldValue("tour")}
                ${
                  form.getFieldValue("start_time")
                    ? `Thời gian bắt đầu: ${form.getFieldValue("start_time")}`
                    : `Ngày: ${
                        form.getFieldValue("date")
                          ? dayjs(form.getFieldValue("date")).format("DD/MM/YYYY")
                          : "Chưa chọn"
                      }
                Giờ: ${form.getFieldValue("time") ?? "Chưa chọn"}`
                }

                Họ và tên: ${form.getFieldValue("name")}
                Email: ${form.getFieldValue("email") || "Không xác định"}
                Số điện thoại: +${getCountryCallingCode(selectedCountry as CountryCode)} ${form.getFieldValue("phone")}
                ${form.getFieldValue("whatsapp") ? `Số WhatsApp: ${form.getFieldValue("whatsapp")}` : ""}
                Nội dung: ${form.getFieldValue("note") ?? ""}
                Quốc tịch: ${form.getFieldValue("nationality") ?? ""}
                Ghi chú: ${form.getFieldValue("note") ?? ""}

                Số lượng người lớn: ${form.getFieldValue("adultQuantity")}
                Số lượng trẻ em: ${form.getFieldValue("childrenQuantity") ?? 0}
                Chiều cao người lớn: ${form.getFieldValue("adultHeight") ?? ""}
                Chiều cao trẻ em: ${form.getFieldValue("childrenHeight") ?? ""}
                ${
                  form.getFieldValue("bike_types") && form.getFieldValue("bike_types").length > 0
                    ? `Loại xe: ${form.getFieldValue("bike_types").join(", ")}`
                    : ""
                }
              `,
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to submit data")
      }

      successNotification("Your request has been sent successfully!")
    } catch (error) {
      console.error("Error:", error)

      errorNotification("Failed to sent request, please try again!")
    } finally {
      setLoading(false)
      setIsModalOpen(false)
      form.resetFields()
    }
  }

  const handleOk = async () => {
    form
      .validateFields()
      .then(() => sendEmailBookTour())
      .catch((errorInfo: any) => {
        console.error("Validation Failed:", errorInfo)
      })
  }

  const fetchSetting = async () => {
    try {
      const response = await getSettings()
      const emailAddress = response.find((item: DataSettingItem) => item.attributes.key === "formBookTourEmail")
        ?.attributes.value
      setEmailConfig(emailAddress)
    } catch (error) {
      console.error("Error fetching setting data:", error)
    }
  }

  useEffect(() => {
    fetchSetting()
    fetchTours()
  }, [])

  useEffect(() => {
    const checkScrollable = () => {
      if (scrollContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
        const canScroll = scrollHeight > clientHeight && scrollTop < scrollHeight - clientHeight - 10
        setCanScrollDown(canScroll)
      }
    }

    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollable)
      setTimeout(checkScrollable, 100)
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", checkScrollable)
      }
    }
  }, [isModalOpen])

  useEffect(() => {
    form.setFieldsValue({
      tour: currentTour?.name,
      start_time: currentTour?.start_time ? dayjs(currentTour.start_time).format("DD/MM/YYYY HH:mm") : undefined,
      bike_types: undefined,
    })
  }, [currentTour, form])

  const onChangeQuantity = (name: string, value: number) => {
    if (name === "adultQuantity") {
      if (value < 1) return
      setAdultQuantity(value)
    } else if (name === "childrenQuantity") {
      setChildrenQuantity(value)
    }

    form.setFieldsValue({
      [name]: value,
    })
  }

  const styleInput =
    "w-full !rounded-xl !text-base leading-[27px] !h-12 py-0 px-4 focus:!border-[#0B9444] hover:!border-[#0B9444]"

  const tourInfo = {
    label: t("Tour information"),
    inputs: [
      {
        name: "tour",
        input: (
          <Select
            placeholder={t("Select a tour")}
            className={cn(styleInput, "px-0 custom-select-antd")}
            suffixIcon={<ChevronDownIcon size={20} strokeWidth={1} color="#8E8E93" />}
            options={tour.map(item => ({
              label: item?.attributes?.name,
              value: item?.attributes?.slug,
            }))}
            onChange={value => {
              form.setFieldsValue({ tour: value.label })
            }}
          />
        ),

        rules: [{ required: true, message: "Please input your tour!" }],
      },
      ...(currentTour?.start_time
        ? [
            {
              name: "start_time",
              input: <Input autoComplete="off" placeholder={t("Start Time")} className={styleInput} disabled />,
            },
          ]
        : [
            {
              name: "date",
              input: (
                <DatePicker
                  placeholder="yy/mm/dd"
                  className={styleInput}
                  format={"YYYY-MM-DD"}
                  minDate={dayjs()}
                  locale={locale === "vi" ? viVN : enUS}
                />
              ),
            },
            {
              name: "time",
              input: (
                <Select
                  placeholder={t("Select start time")}
                  className={cn(styleInput, "px-0 custom-select-antd")}
                  suffixIcon={<ChevronDownIcon size={20} strokeWidth={1} color="#8E8E93" />}
                  options={getTimeOptions()}
                />
              ),
            },
          ]),
    ],
  }

  const contactInfo = {
    label: t("Contact information"),
    inputs: [
      {
        name: "name",
        input: (
          <Input
            autoComplete="off"
            placeholder={t("Your name")}
            className={styleInput}
            style={{ fontFamily: "Montserrat" }}
          />
        ),
        rules: [{ required: true, message: t("Please enter your name!") }],
      },
      {
        name: "email",
        input: (
          <Input autoComplete="off" placeholder="Email" className={styleInput} style={{ fontFamily: "Montserrat" }} />
        ),
        rules: [
          { required: true, message: t("Please enter your email!") },
          { type: "email" as const, message: t("Please enter a valid email!") },
        ],
      },
      {
        name: "phone",
        custom: (
          <div className="flex items-center gap-2">
            <Select
              value={selectedCountry}
              onChange={value => setSelectedCountry(value)}
              className="[&_.ant-select-selector]:py-3 [&_.ant-select-selector]:px-4 [&_.ant-select-selector]:bg-white [&_.ant-select-selector]:rounded-xl [&_.ant-select-selector]:border [&_.ant-select-selector]:border-[#00000014] [&_.ant-select-selector]:text-base [&_.ant-select-selector]:leading-[27px] [&_.ant-select-selector]:h-full"
              style={{ height: "48px", minWidth: "150px", maxWidth: "200px" }}
              virtual={false}
            >
              {countries.map(code => (
                <Select.Option key={code} value={code}>
                  {code} (+{getCountryCallingCode(code)})
                </Select.Option>
              ))}
            </Select>
            <Form.Item
              name="phone"
              className="mb-0 flex-1"
              rules={[{ required: true, message: t("Please enter your phone number!") }]}
            >
              <Input
                autoComplete="off"
                type="tel"
                placeholder={t("Phone")}
                className={styleInput}
                style={{ maxHeight: "48px", fontFamily: "Montserrat" }}
              />
            </Form.Item>
          </div>
        ),
      },
      {
        name: "whatsapp",
        input: (
          <Input
            autoComplete="off"
            type="tel"
            placeholder={t("Enter your WhatsApp number")}
            className={styleInput}
            style={{ fontFamily: "Montserrat" }}
          />
        ),
      },
      {
        name: "nationality",
        input: (
          <Input
            autoComplete="off"
            placeholder={t("Nationality")}
            className={styleInput}
            style={{ fontFamily: "Montserrat" }}
          />
        ),
      },
      {
        name: "note",
        input: (
          <Input.TextArea
            autoComplete="off"
            placeholder={t("Special Notes (allergies, special requests, ...)")}
            style={{ resize: "none", height: "96px", borderRadius: 12, fontFamily: "Montserrat" }}
            className="focus:border-[#0B9444] hover:border-[#0B9444]"
          />
        ),
      },
    ],
  }

  const participantInfo = {
    label: t("Participant Information"),
    inputs: [
      {
        custom: (
          <div className="flex flex-row gap-4">
            <Form.Item name="adultQuantity" className="w-full mb-0">
              <div className="flex flex-row gap-2 items-center justify-center border border-[#0000001F] rounded-xl p-2 w-full focus-within:border-[#0B9444] transition-colors duration-300 hover:border-[#0B9444]">
                <Plus
                  size={20}
                  color="#8E8E93"
                  className="min-w-5 cursor-pointer hover:scale-105 transition-all duration-300"
                  onClick={() => onChangeQuantity("adultQuantity", (adultQuantity || 0) + 1)}
                />
                <input
                  autoComplete="off"
                  type="number"
                  placeholder={t("Adult")}
                  className="w-full text-center border-none focus:border-none focus:outline-none focus:ring-0 placeholder:text-[#8E8E93]"
                  min={1}
                  value={adultQuantity}
                  onChange={e => onChangeQuantity("adultQuantity", Number(e.target.value))}
                />
                <Minus
                  size={20}
                  color="#8E8E93"
                  className="min-w-5 cursor-pointer hover:scale-105 transition-all duration-300"
                  onClick={() => onChangeQuantity("adultQuantity", Math.max(1, (adultQuantity || 1) - 1))}
                />
              </div>
            </Form.Item>

            <Form.Item name="childrenQuantity" className="w-full mb-0">
              <div className="flex flex-row gap-2 items-center justify-center border border-[#0000001F] rounded-xl p-2 w-full focus-within:border-[#0B9444] transition-colors duration-300 hover:border-[#0B9444]">
                <Plus
                  size={20}
                  color="#8E8E93"
                  className="min-w-5 cursor-pointer hover:scale-105 transition-all duration-300"
                  onClick={() => onChangeQuantity("childrenQuantity", (childrenQuantity || 0) + 1)}
                />
                <input
                  autoComplete="off"
                  type="number"
                  placeholder={t("Children")}
                  className="w-full text-center border-none focus:border-none focus:outline-none focus:ring-0 placeholder:text-[#8E8E93]"
                  min={0}
                  value={childrenQuantity}
                  onChange={e => onChangeQuantity("childrenQuantity", Number(e.target.value))}
                />
                <Minus
                  size={20}
                  color="#8E8E93"
                  className="min-w-5 cursor-pointer hover:scale-105 transition-all duration-300"
                  onClick={() => onChangeQuantity("childrenQuantity", Math.max(0, (childrenQuantity || 0) - 1))}
                />
              </div>
            </Form.Item>
          </div>
        ),
      },

      {
        custom: (
          <p className="text-base leading-[27px] text-[#8E8E93] italic font-normal">
            {t("Note: Applicable for children aged 3 and above. However, children over 6 years old are recommended.")}
          </p>
        ),
      },
      {
        name: "adultHeight",
        input: (
          <Input
            autoComplete="off"
            className={styleInput}
            placeholder={t("Adult height")}
            style={{ fontFamily: "Montserrat" }}
          />
        ),
      },
      {
        name: "childrenHeight",
        input: (
          <Input
            autoComplete="off"
            className={styleInput}
            placeholder={t("Children height")}
            style={{ fontFamily: "Montserrat" }}
          />
        ),
      },
      ...(currentTour?.show_bike_type &&
      currentTour?.bike_types?.data &&
      Array.isArray(currentTour.bike_types.data) &&
      currentTour.bike_types.data.length > 0
        ? [
            {
              name: "bike_types",
              input: (
                <Select
                  mode="multiple"
                  placeholder={t("Bike Type")}
                  className={cn(styleInput, "px-0 custom-select-antd")}
                  suffixIcon={<ChevronDownIcon size={20} strokeWidth={1} color="#8E8E93" />}
                  options={currentTour.bike_types.data
                    .map((item: BikeTypeOption) => ({
                      label: item.attributes?.name || "",
                      value: item.attributes?.name || "",
                    }))
                    .filter(option => option.label && option.value)}
                  style={{ fontFamily: "Montserrat" }}
                  tagRender={props => {
                    const { label, value, closable, onClose } = props
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          backgroundColor: "#c9e4a5",
                          color: "#242F3E",
                          fontSize: "16px",
                          lineHeight: "27px",
                          height: "40px",
                          border: "1px solid transparent",
                          fontFamily: "Montserrat",
                          minHeight: "40px",
                        }}
                      >
                        <span>{label}</span>
                        {closable && (
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#8E8E93",
                              transition: "color 0.3s",
                              display: "flex",
                              alignItems: "center",
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = "#242F3E")}
                            onMouseLeave={e => (e.currentTarget.style.color = "#8E8E93")}
                            onClick={onClose}
                          >
                            <X size={14} />
                          </span>
                        )}
                      </span>
                    )
                  }}
                />
              ),
            },
          ]
        : []),
      {
        custom: (
          <p className="text-base leading-[27px] text-[#8E8E93] italic font-normal">
            {t(
              "Children under 12 years old or those who are not confident riding on their own should use a tag-along.",
            )}
          </p>
        ),
      },
    ],
  }

  return (
    <>
      {contextHolderNotification}
      <Modal
        open={isModalOpen}
        centered
        onOk={handleOk}
        width={1000}
        onCancel={() => {
          setIsModalOpen(false)
          form.resetFields()
          setSelectedCountry("VN")
        }}
        footer={null}
        closeIcon={<X size={24} color="#999999" />}
        classNames={{
          content: "!rounded-2xl",
        }}
      >
        <div className="px-5 pt-10 pb-2.5 mt-17 lg:pb-12 lg:pt-[58px] lg:px-16">
          <p className="text-2xl leading-7 font-bold text-[#242F3E] text-center uppercase lg:text-[28px] lg:leading-[36px] pb-2">
            {t("Book this tour")}
          </p>

          <div className="relative">
            <ScrollIndicator canScrollDown={canScrollDown} />

            <div
              ref={scrollContainerRef}
              className="mx-auto flex flex-col gap-4 max-h-[63vh] lg:max-h-[65vh] overflow-y-scroll py-4 lg:py-14 scrollbar-thin scrollbar-thumb-[#0B9444]/20 scrollbar-track-transparent hover:scrollbar-thumb-[#0B9444]/40"
            >
              <Form
                form={form}
                layout="vertical"
                className="flex flex-col gap-8 lg:flex-row"
                initialValues={{ tour: currentTour?.slug, adultQuantity: 1, childrenQuantity: 0 }}
              >
                <div className="flex flex-col gap-8 lg:gap-16 lg:w-1/2">
                  <InfomationFormTour label={tourInfo.label} inputs={tourInfo.inputs} />
                  <InfomationFormTour label={contactInfo.label} inputs={contactInfo.inputs} />
                </div>

                <div className="flex flex-col gap-8 lg:gap-16 lg:w-1/2">
                  <InfomationFormTour label={participantInfo.label} inputs={participantInfo.inputs} />

                  {/* <div className="flex flex-col gap-4">
                    <p className="text-base leading-6 font-semibold text-[#242F3E] uppercase">
                      {t("Tour Summary & Booking Confirmation")}
                    </p>
                  </div> */}
                </div>
              </Form>
            </div>
          </div>

          <div
            onClick={() => !loading && handleOk()}
            className={`text-white bg-[#0B9444] w-full h-12 rounded-xl text-center text-base leading-5.5 font-semibold flex flex-row gap-4 justify-center border border-transparent transition-all duration-300 items-center mt-2 ${
              loading ? "cursor-not-allowed opacity-80" : "cursor-pointer"
            } hover:bg-white hover:text-[#0B9444] hover:border-[#0B9444]`}
          >
            {loading && <Spin size="default" indicator={<LoadingOutlined style={{ color: "white" }} />} />}
            {t("Book now")}
          </div>
        </div>
      </Modal>
    </>
  )
}

export default FormBooking
